import React from "react";

const ViewOrderDetails = ({ order, closeView }) => {
  // Extract status update times for display
  const statusUpdates = order.statusUpdatedTimes || {};
  const getStatusDate = (status) =>
    statusUpdates[status]
      ? new Date(statusUpdates[status]).toLocaleDateString("en-GB")
      : "Not Updated";

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-[#2b6e73]">
          Order Details - {order.salesOrderNo || "NA"}
        </h3>
        <button
          className="text-[#2b6e73] border border-[#2b6e73] rounded-full w-9 h-9 flex items-center justify-center"
          onClick={closeView}
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-semibold text-[#2b6e73] mb-2">
            Order Information
          </h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                RFQ No
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.rfqNo || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Quotation No
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.quatationOrderNo || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Order Status
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.salesOrderStatus || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Expected Delivery
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.deliveryDate
                  ? new Date(order.deliveryDate).toLocaleDateString("en-GB")
                  : "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Sales Representative
              </span>
              <span className="w-[10px] text-gray-600"> :</span>
              <span className="font-medium text-gray-800">
                {order.assigneeName || "NA"}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-gray-50 p-4 rounded-lg overflow-hidden">
          <h4 className="text-lg font-semibold text-[#2b6e73] mb-2">
            Customer Information
          </h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Name
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.customerName || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Company
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.companyName || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Mobile Number
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800">
                {order.customerMobileNo || "NA"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="font-semibold text-gray-600 min-w-[120px]">
                Email
              </span>
              <span className="w-[10px] text-gray-600">:</span>
              <span className="font-medium text-gray-800 break-words max-w-[60%] md:max-w-[150px]">
                {order.customerEmail || "NA"}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping Address & Order Status */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-[#2b6e73] mb-2">
              Shipping Address
            </h4>
            <p className="text-sm font-medium text-gray-800">
              {order.deliverAddress || order.address || "NA"}
            </p>
          </div>
          <h4 className="text-md font-semibold text-[#2b6e73] mb-2">
            Order Status
          </h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 rounded bg-green-100">
              <p className="text-sm font-medium text-gray-800">Created</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Created")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-yellow-100">
              <p className="text-sm font-medium text-gray-800">In Synthesis</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("In Synthesis")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-blue-100">
              <p className="text-sm font-medium text-gray-800">
                Waiting For RM
              </p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Waiting For RM")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-orange-100">
              <p className="text-sm font-medium text-gray-800">Ready To Pack</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Ready To Pack")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-red-100">
              <p className="text-sm font-medium text-gray-800">Packing</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Packing")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-blue-100">
              <p className="text-sm font-medium text-gray-800">Shipped</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Shipped")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-yellow-100">
              <p className="text-sm font-medium text-gray-800">
                Out For Delivery
              </p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Out For Delivery")}
              </p>
            </div>
            <div className="flex justify-between items-center p-2 rounded bg-green-100">
              <p className="text-sm font-medium text-gray-800">Delivered</p>
              <p className="text-sm text-gray-600">
                {getStatusDate("Delivered")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="mt-6 bg-gray-50 p-4 rounded-lg ">
        <h4 className="text-md font-semibold text-[#2b6e73] mb-2">
          Order Items
        </h4>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-2 text-gray-700 text-left">Product</th>
              <th className="p-2 text-gray-700 text-left">CAS Number</th>
              <th className="p-2 text-gray-700 text-left">Catalog Number</th>
              <th className="p-2 text-gray-700 text-left">HSN Code</th>
              <th className="p-2 text-gray-700 text-left">Quantity</th>
              {/* <th className="p-2 text-gray-700 text-left">Total</th> */}
            </tr>
          </thead>
          <tbody>
            {order.salesOrderProducts && order.salesOrderProducts.length > 0 ? (
              order.salesOrderProducts.map((item, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2 text-gray-800">
                    {item.productName || "NA"}
                  </td>
                  <td className="p-2 text-gray-800">
                    {item.casNumber || "NA"}
                  </td>
                  <td className="p-2 text-gray-800">
                    {item.catalogNumber || "NA"}
                  </td>
                  <td className="p-2 text-gray-800">{item.hsnCode || "NA"}</td>
                  <td className="p-2 text-gray-800">
                    {item.quantity} {item.uom || ""}
                  </td>
                  {/* <td className="p-2 text-gray-800">{item.totalPrice || "NA"}</td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-2 text-center text-gray-600">
                  No items available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewOrderDetails;